import type { CSSProperties } from 'react';
import { Link } from 'react-router';
import { campaignPath, formatCount } from '../lib/portal';
import { Loading } from '../components/Loading';
import { accentHex, brandColors } from '../lib/accent';
import { useBrandBySlug, useBrandCampaigns, type CampaignRow } from '../data/brands';
import { useLazyAssetUrl } from '../lib/assetUrls';
import { formatDateTime } from '../lib/format';
import { NotFoundPage } from './NotFoundPage';

function CampaignTile({ campaign, featured }: { campaign: CampaignRow; featured: boolean }) {
  const { ref, url } = useLazyAssetUrl(campaign.id, campaign.coverAssetId ?? null, 'large');
  const color = accentHex(campaign.accent);
  return (
    <Link
      ref={(el) => {
        ref.current = el;
      }}
      to={campaignPath(window.location.host, campaign.clientSlug, campaign.slug)}
      className={`portal-tile ${featured ? 'portal-tile--feat' : ''}`}
      style={{ '--tile': color } as CSSProperties}
    >
      {campaign.coverAssetId && url ? (
        <img src={url} alt="" loading="lazy" decoding="async" />
      ) : null}
      <span className="portal-tile__shade" aria-hidden="true" />
      <span className="portal-tile__title">{campaign.name}</span>
      <span className="portal-tile__bar t-meta">
        <span>
          {campaign.status === 'published'
            ? `Publicada ${formatDateTime(campaign.publishedAt)}`
            : 'Borrador (solo admin)'}
        </span>
        <span>Ver campaña →</span>
      </span>
    </Link>
  );
}

/** Portal de una marca: {marca}.orbyte.mx (o /{marca} por ruta). */
export function BrandPortalPage({ brandSlug }: { brandSlug: string }) {
  const brand = useBrandBySlug(brandSlug);
  const brandId = brand.status === 'ready' ? brand.data?.id : undefined;
  const campaigns = useBrandCampaigns(brandId);

  if (brand.status === 'loading') return <Loading />;
  if (brand.status !== 'ready' || !brand.data) return <NotFoundPage />;
  const b = brand.data;
  const palette = brandColors(b.accent, b.accent2);
  const list =
    campaigns.status === 'ready'
      ? [...campaigns.data].sort((x, y) => {
          const tx = (x.publishedAt as { toMillis?: () => number } | null)?.toMillis?.() ?? 0;
          const ty = (y.publishedAt as { toMillis?: () => number } | null)?.toMillis?.() ?? 0;
          return ty - tx || x.name.localeCompare(y.name, 'es');
        })
      : [];

  return (
    <main
      className="portal"
      style={
        {
          '--accent': palette.primary,
          '--on-accent': palette.onPrimary,
          '--accent2': palette.secondary,
          '--on-accent2': palette.onSecondary,
        } as CSSProperties
      }
    >
      <header className="portal-hero">
        <div className="portal-hero__block">
          <p className="t-nav portal-hero__kicker">Brands&amp;People ×</p>
          <h1 className="portal-hero__name">{b.name}</h1>
          <p className="t-text portal-hero__meta">
            {formatCount(list.length, 'campaña', 'campañas')}
          </p>
        </div>
        <nav className="portal-circle t-text" aria-label="Campañas">
          {list.slice(0, 6).map((c) => (
            <Link key={c.id} to={campaignPath(window.location.host, c.clientSlug, c.slug)}>
              {c.name}
            </Link>
          ))}
          {list.length === 0 ? <span>Sin campañas todavía</span> : null}
        </nav>
      </header>

      {campaigns.status === 'loading' ? <Loading /> : null}
      {campaigns.status === 'ready' && list.length === 0 ? (
        <p className="page__pad empty__text">
          Todavía no tienes campañas disponibles de {b.name}. Cuando la agencia publique una,
          aparecerá aquí.
        </p>
      ) : null}

      <section className="portal-grid" aria-label={`Campañas de ${b.name}`}>
        {list.map((c, i) => (
          <CampaignTile key={c.id} campaign={c} featured={i === 0 || i % 5 === 3} />
        ))}
      </section>
    </main>
  );
}
